import { useState } from 'react';
import type { ReactNode } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { cn } from '@/lib/utils';

interface HoverTipProps {
  /** Content shown on hover — plain text or a rich node. */
  content: ReactNode;
  /** The hover target (e.g. an initials badge). */
  children: ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
  /** Extra classes on the floating box. */
  className?: string;
}

/**
 * Hover info chip built on Radix Popover (PRODOPSAI-538).
 *
 * Uses `Popover.Anchor` rather than `Popover.Trigger` so it never intercepts
 * the click of an enclosing button (the overview cards are clickable). The box
 * is portalled to `document.body` and `collisionPadding` keeps it inside the
 * viewport, so — unlike the CSS `Tooltip` — it never clips at the page edge for
 * the leftmost/rightmost badges.
 *
 * Hover-only by design (focus is intentionally not moved, and the same
 * name/role is fully visible on the team detail page for keyboard / SR users).
 */
export function HoverTip({ content, children, side = 'top', className }: Readonly<HoverTipProps>) {
  const [open, setOpen] = useState(false);

  return (
    <Popover.Root open={open} onOpenChange={setOpen} modal={false}>
      {/* Handlers live on the Radix Anchor component (not a raw DOM element):
          this is a hover-only enhancement, and the trigger must NOT be a
          focusable/interactive element because it renders inside an already
          clickable card. The accessible name + role is provided by the child
          badge (role="img" + aria-label), and the same data is on the detail
          page for keyboard / screen-reader users. */}
      <Popover.Anchor
        className="inline-flex"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        {children}
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          side={side}
          sideOffset={6}
          collisionPadding={8}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          className={cn(
            'pointer-events-none z-50 w-max max-w-xs rounded-md border border-border bg-popover px-2 py-1 text-xs text-popover-foreground shadow-md',
            className,
          )}
        >
          {content}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
